import type {
  LaserficheInspectField,
  LaserficheInspectTag,
  LaserficheInspectTemplate,
  LaserficheMetadata,
  LaserfichePushType,
  LaserfichePutField,
  LaserfichePutMetadata,
  LaserfichePutTag,
  LaserfichePutTemplate,
} from "@repo/types";
import { LaserficheFieldMapper } from "./LaserficheFieldMapper";
import { LaserficheTagPicker } from "./LaserficheTagPicker";
import {
  type LaserficheFieldSourceDetails,
  laserficheFieldSources,
} from "./laserficheFieldSources";
import { useMemo, useState } from "react";
import { cn } from "@repo/shared/utils";
import { Button } from "@repo/ui/components/Base/Button";
import { LaserficheTemplatePicker } from "./LaserficheTemplatePicker";
import { CheckboxInput } from "@repo/ui/components/Base/CheckboxInput";
import { useFutureCallback } from "@repo/ui/components/Base/hooks/useFutureCallback";
import { runSaveAll, SaveAllContext } from "./saveAllContext";
import { useConstComponent } from "@repo/ui/components/Base/hooks/useNestedComponent";
import { useBlockUnload } from "@repo/ui/components/Base/hooks/useBlockUnload";

export interface LaserficheMetadataProps {
  disabled?: boolean;
  availableMetadata: {
    fields: LaserficheInspectField[];
    tags: LaserficheInspectTag[];
    templates: LaserficheInspectTemplate[];
  };
  pushType: LaserfichePushType;
  initialMetadata?: LaserficheMetadata;
  className?: string;
  enableFields?: string;
  onSave?: (config: LaserfichePutMetadata) => void | Promise<void>;
  onDelete?: () => void;
}

export const LaserficheMetadataPush = ({
  disabled,
  availableMetadata,
  pushType,
  initialMetadata,
  className,
  enableFields = "Enabled",
  onSave,
  onDelete,
}: LaserficheMetadataProps) => {
  const [saved, setSaved] = useState(true);
  const [enabled, setEnabled] = useState(!!initialMetadata?.enabled);

  useBlockUnload(saved);

  // LaserficheTag -> LaserfichePutTag
  const [tags, setTags] = useState<LaserfichePutTag[]>(
    () =>
      initialMetadata?.tags.map(({ tagName: _, ...tag }) => ({ ...tag })) ?? []
  );
  // LaserficheTemplate -> LaserfichePutTemplate
  const [template, setTemplate] = useState<LaserfichePutTemplate | null>(() =>
    initialMetadata?.template
      ? (({ templateName: _, ...template }) => ({
          ...template,
          templateFieldAssignments: template.templateFieldAssignments.map(
            ({ fieldName: _, ...field }) => ({ ...field })
          ),
        }))(initialMetadata.template)
      : null
  );
  // LaserficheField -> LaserfichePutField
  const [fields, setFields] = useState<LaserfichePutField[]>(
    () =>
      initialMetadata?.fields.map(({ fieldName: _, ...field }) => ({
        ...field,
      })) ?? []
  );

  const [loading, setLoading] = useState(false);

  const handleSave = useFutureCallback(async () => {
    setLoading(true);
    await onSave?.({ enabled, tags, template, fields });
    setLoading(false);
    setSaved(true);
  });

  const SaveAll = SaveAllContext.useAsIntermediate({
    order: 1,
    save: handleSave,
  });
  const Provider = useConstComponent(SaveAll.Provider);

  const availableSources = useMemo(
    () =>
      Object.keys(laserficheFieldSources[pushType]).map((key) => ({
        ...(laserficheFieldSources[pushType][
          key as never
        ] as LaserficheFieldSourceDetails),
        key,
      })),
    [pushType]
  );

  return (
    <Provider>
      <div className={cn("flex flex-col gap-5", className)}>
        {enableFields && (
          <CheckboxInput
            checked={enabled}
            onCheckedChange={(checked) => {
              setEnabled(!!checked);
              setSaved(false);
            }}
            orientation="horizontal"
            label={enableFields}
          />
        )}

        <div
          className={cn(
            "flex flex-col gap-5",
            enableFields && !enabled && "hidden"
          )}
        >
          <div className="flex flex-col gap-2">
            <p className="text-lg">Apply the following tags:</p>
            <LaserficheTagPicker
              disabled={disabled || loading}
              availableTags={availableMetadata.tags}
              value={tags}
              onChange={(newSelection) => {
                setTags(newSelection);
                setSaved(false);
              }}
            />
          </div>

          <div className="flex flex-col gap-2">
            <p className="text-lg">Apply the following template:</p>
            <LaserficheTemplatePicker
              disabled={disabled || loading}
              availableTemplates={availableMetadata.templates}
              availableFields={availableMetadata.fields}
              availableSources={availableSources}
              value={template}
              onChange={(value) => {
                setTemplate(value);
                setSaved(false);
              }}
            />
          </div>

          <div className="flex flex-col gap-2">
            <p className="text-lg">Assign the following fields:</p>
            <LaserficheFieldMapper
              disabled={disabled || loading}
              availableFields={availableMetadata.fields}
              availableSources={availableSources}
              value={fields}
              onChange={(value) => {
                setFields(value);
                setSaved(false);
              }}
            />
          </div>
        </div>

        <div className="flex flex-row justify-between items-start">
          {onDelete ? (
            <Button variant="destructive" onClick={onDelete}>
              Remove
            </Button>
          ) : (
            <div />
          )}

          <div className="flex flex-col gap-2">
            <Button
              disabled={disabled || loading}
              className={cn("self-end", saved && "opacity-80")}
              onClick={async () => {
                await runSaveAll(SaveAll);
                await handleSave();
              }}
            >
              Save
            </Button>

            {saved && <p className="text-center">Saved!</p>}
          </div>
        </div>
      </div>
    </Provider>
  );
};
